/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOpenComments';

const ActionOpenCommentsManager: ActionManagerFn<'ActionOpenComments'> = (
  platform: Platform,
): ActionManager<'ActionOpenComments'> => {
  return {
    ...baseActionManager(platform, 'ActionOpenComments'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Comments Item ID',
        type: 'ItemID',
        initialFilter: 'Comments',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Comments') {
            return `Item id '${value}' doesn't belong to a Comments Component`;
          }
          return '';
        },
      },
    },
    name: 'Open Comments',
    isItemIdUsed: (item, itemId) => item.props.targetItemId === itemId,
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'comments' },
    description: `**Overview**<br/>
The Open Comments action is a specific UI interaction component designed to trigger the display of a "Comments" component. This target component is typically a specialized drawer, panel, or sidebar used to manage user discussions, notes, or feedback threads associated with specific data records.<br/><br/>
This action allows developers to control exactly when the collaboration interface appears, rather than having it always visible. It is tightly coupled with the Comments component type, employing strict validation to ensure it only operates on the correct UI element.

**Key Features**<br/>
* **Targeted Trigger:** Specifically designed to activate a Comments component, typically expanding a drawer or modal to reveal the conversation history.
* **Strict Type Validation:** Implements validateItemId to verify that the provided targetItemId corresponds exactly to a component of type 'Comments', preventing runtime errors from targeting incompatible elements.
* **Registry Filtering:** The property picker uses initialFilter: 'Comments' to streamline the configuration process, restricting the dropdown to show only valid comment components available on the page.
* **Dependency Tracking:** Implements isItemIdUsed to correctly register the dependency between the action and the target UI component.

**Properties**<br/>
* **Comments Item ID (targetItemId):** A property of type ItemID that identifies the specific Comments component to open.
  * *Validation:* Checks if the item exists and is of type Comments.

**Use Cases**<br/>
* **Collaboration Context:** Placing a "Discuss" button on a Project Details page that, when clicked, opens the comments drawer for that specific project.
* **Grid Integration:** Adding an action column with a "Chat" icon to a Data Grid; clicking the icon opens the comments thread associated with that specific row's record.`,
  };
};

export default ActionOpenCommentsManager;
